import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import toast from 'react-hot-toast';

import { useBulkApproveQueueItemsMutation } from '@/store/api/services/marketing/queue';
import { ClickableText, type ModalChildrenProps } from '@bitstopco/bitstop-theme';

import type { QueueItem } from '@/types';

const INELIGIBLE_REASONS: Partial<Record<QueueItem['status'], string>> = {
  approved: 'Already approved',
  rejected: 'Already rejected',
  completed: 'Already completed',
  error: 'Error state',
  obsolete: 'Obsolete',
  conflict: 'Has conflict',
};

const BulkApproveQueue = ({ hide, props }: ModalChildrenProps) => {
  const selectedItems = (props?.data?.selectedItems ?? []) as QueueItem[];
  const [bulkApprove, { isLoading, isSuccess }] = useBulkApproveQueueItemsMutation();

  const { eligible, ineligible } = useMemo(() => {
    const eligible: QueueItem[] = [];
    const ineligible: { item: QueueItem; reason: string }[] = [];
    for (const item of selectedItems) {
      const reason = INELIGIBLE_REASONS[item.status];
      if (reason) ineligible.push({ item, reason });
      else eligible.push(item);
    }
    return { eligible, ineligible };
  }, [selectedItems]);

  const handleSubmit = async () => {
    try {
      await bulkApprove({ ids: eligible.map((i) => i.id) }).unwrap();
      toast.success(`${eligible.length} queue item(s) published successfully.`);
    } catch (_err) {
      toast.error('Failed to publish queue items.');
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {!isSuccess ? (
          <>
            <Typography variant="h6" mb={1}>
              Publish {eligible.length} queue item(s)?
            </Typography>
            {ineligible.length > 0 && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {ineligible.length} selected item(s) will be skipped — only <b>Pending</b> items can
                be approved.
              </Typography>
            )}
            <Divider />
            <TableContainer
              sx={{ mt: 2, opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligible.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <ClickableText
                          fixIcon
                          text={item.location_name ?? item.location_id.toString()}
                          clipboardValue={item.location_id.toString()}
                          name="Location ID"
                          to={`/location/${item.location_id}`}
                        />
                      </TableCell>
                      <TableCell>{item.action}</TableCell>
                      <TableCell>{item.status}</TableCell>
                    </TableRow>
                  ))}
                  {ineligible.map(({ item, reason }) => (
                    <Tooltip key={item.id} title={reason} placement="left">
                      <TableRow sx={{ opacity: 0.4 }}>
                        <TableCell sx={{ textDecoration: 'line-through' }}>
                          {item.location_name ?? item.location_id.toString()}
                        </TableCell>
                        <TableCell sx={{ textDecoration: 'line-through' }}>{item.action}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.disabled">
                            {reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography variant="h6">
            {eligible.length} queue item(s) published successfully.
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isSuccess ? (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={eligible.length === 0}
            >
              Publish {eligible.length > 0 ? eligible.length : ''} item(s)
            </LoadingButton>
          </>
        ) : (
          <Button fullWidth onClick={hide}>
            Back to Queue
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkApproveQueue;
